import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type { SensorData } from '../Components/Dashboard/dashboardTypes';
import {
  DRIVEBASE_SYSTEM_STATE,
  isDrivebasePythonProgramBusy,
  isDrivebaseRuntimeBusyForFirmwareActions,
  isDrivebaseSystemStateBusy,
} from '../robot/drivebase-system-state';
import { RobotActions } from '../utils/constants';
import type { RootState } from './Store';

interface FirmwareProgress {
  libraryId?: string;
  assetId?: string;
  state?: string;
  percent?: number;
  [key: string]: unknown;
}

interface RobotSliceState {
  robotState: string;
  connectionType: string;
  programActionsReady: boolean;
  runtimeProgramState: string | null;
  discovery: {
    discoveryState: string | null;
    robotInfo: {
      name: string;
      [key: string]: unknown;
    };
    message: Record<string, unknown>;
  };
  firmware: {
    status: string | null;
    firmwareState: string | null;
    errorCode: string | number | null;
    progress: FirmwareProgress | number | null;
    optionalUpdate: unknown;
  };
  batteryCharging: boolean;
  batteryPercentage: number | null;
  powerState: string | null;
  powerLevel: string | null;
  liveSensorData: SensorData | null;
  command?: {
    command: unknown;
    args: unknown[];
  };
}

// This reduce is to track things the robot can do, and the robots state
const initialState: RobotSliceState = {
  robotState: RobotActions.STATE.DISCONNECTED,
  connectionType: RobotActions.STATE.CONNECTION_TYPE.BLE,
  programActionsReady: false,
  runtimeProgramState: null as string | null,
  discovery: {
    discoveryState: null,
    robotInfo: { name: '' },
    message: {},
  },
  firmware: {
    status: null,
    firmwareState: null,
    errorCode: null,
    progress: null,
    optionalUpdate: null,
  },
  batteryCharging: false,
  batteryPercentage: null as number | null,
  powerState: null as string | null,
  powerLevel: null as string | null,
  liveSensorData: null,
};

const initialBatteryTelemetry = {
  batteryCharging: initialState.batteryCharging,
  batteryPercentage: initialState.batteryPercentage,
  powerState: initialState.powerState,
  powerLevel: initialState.powerLevel,
};

export const robotSlice = createSlice({
  name: 'robotCommand',
  initialState,
  reducers: {
    onUpdateRobotState: (state, { payload }) => ({ ...state, robotState: payload }),
    onUpdateConnectionType: (state, { payload }) => ({ ...state, connectionType: payload }),
    onUpdateRobotStateAndConnectionType: (state, { payload }) => ({
      ...state,
      robotState: payload.robotState,
      connectionType: payload.connectionType,
    }),
    onFirmwareStateChange: (state, { payload }) => ({
      ...state,
      firmware: {
        ...state.firmware,
        firmwareState: payload.firmwareState,
        errorCode: payload.errorCode,
        progress: Object.prototype.hasOwnProperty.call(payload, 'progress')
          ? payload.progress
          : payload.firmwareState === RobotActions.FIRMWARE_UPDATE.START_DOWNLOAD ||
            payload.firmwareState === RobotActions.FIRMWARE_UPDATE.FINALIZE_UPDATES
          ? null
          : state.firmware.progress,
      },
    }),
    onUpdateFirmwareStatus: (state, { payload }) => ({
      ...state,
      firmware: {
        ...state.firmware,
        status: payload,
      },
    }),
    onUpdateDiscovery: (state, { payload }) => ({
      ...state,
      discovery: {
        ...state.discovery,
        discoveryState: payload.discoveryState,
        robotInfo: payload.robotInfo,
      },
    }),
    onUpdateFirmwareProgress: (state, { payload }) => ({
      ...state,
      firmware: {
        ...state.firmware,
        progress: payload,
      },
    }),
    onUpdateFirmwareOption: (state, { payload }) => ({
      ...state,
      firmware: {
        ...state.firmware,
        optionalUpdate: payload,
      },
    }),
    onUpdateProgramActionsReady: (state, { payload }) => ({
      ...state,
      programActionsReady: payload === true,
    }),
    onUpdateRuntimeProgramState: (state, { payload }: PayloadAction<string | null>) => ({
      ...state,
      runtimeProgramState: payload,
    }),
    onConnect: (state, { payload }) => ({
      ...state,
      robotState: payload.robotState,
      programActionsReady: false,
      runtimeProgramState: null,
      ...initialBatteryTelemetry,
    }),
    onDisconnect: (state) => ({
      ...state,
      robotState: RobotActions.STATE.DISCONNECTED,
      programActionsReady: false,
      runtimeProgramState: null,
      ...initialBatteryTelemetry,
      liveSensorData: null,
      command: {
        command: null,
        args: [],
      },
    }),
    onUpdatePowerState: (
      state,
      {
        payload,
      }: PayloadAction<{
        batteryCharging: boolean;
        batteryPercentage?: number | null;
        powerState: string | null;
        powerLevel: string | null;
      }>
    ) => ({
      ...state,
      batteryCharging: payload.batteryCharging,
      batteryPercentage: payload.batteryPercentage ?? null,
      powerState: payload.powerState,
      powerLevel: payload.powerLevel,
    }),
    onUpdateLiveSensorData: (state, { payload }) => ({
      ...state,
      liveSensorData: payload,
    }),
  },
});

export const selectDiscoveryState = (state: RootState) => state.robot.discovery;
export const selectRuntimeProgramState = (state: RootState) => state.robot.runtimeProgramState;
export const selectIsRobotRuntimeExecutingPython = (state: RootState) => state.robot.runtimeProgramState === DRIVEBASE_SYSTEM_STATE.EXECUTING_PYTHON;

export const selectIsDrivebaseRuntimeBusy = (state: RootState) => {
  const runtimeState = state.robot.runtimeProgramState;
  return isDrivebaseSystemStateBusy(runtimeState);
};

export const selectIsDrivebaseDistributingFirmware = (state: RootState) =>
  state.robot.runtimeProgramState === DRIVEBASE_SYSTEM_STATE.DISTRIBUTING_CORE_FIRMWARE ||
  state.robot.runtimeProgramState === DRIVEBASE_SYSTEM_STATE.DISTRIBUTING_ACCESSORY_FIRMWARE;

export const selectIsProgramActive = (state: RootState) =>
  isDrivebasePythonProgramBusy(state.robot.runtimeProgramState) ||
  state.project.programState === RobotActions.PROGRAM.RUN ||
  // STOP is a command/request state and can be left behind by non-runtime flows; runtime busy state covers real stops.
  state.project.programState === RobotActions.PROGRAM.STARTING ||
  state.project.programState === RobotActions.PROGRAM.STARTED;

export const selectAreDrivebaseCommandActionsDisabled = (state: RootState) => selectIsProgramActive(state) || selectIsDrivebaseRuntimeBusy(state);

export const selectAreDrivebaseFirmwareActionsDisabled = (state: RootState) =>
  selectIsProgramActive(state) || isDrivebaseRuntimeBusyForFirmwareActions(state.robot.runtimeProgramState);

export default robotSlice.reducer;
